import { useEffect, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import type { Message } from '@/types';

const POLL_INTERVAL_MS = 3_000;

// Recorder conversations are created with WithPublic(aws.Username) so messages
// land on the tenant partition, not the per-user composite partition. We bypass
// useChatWorker (which hardcodes user=true) and fetch the tenant partition
// directly. Polls every 3s — VITE_WS_URL is unset in dev so WS-driven updates
// don't fire; polling is the only reliable path.
export function useRecorderMessages(conversationId: string): Message[] {
  const axios = useAxios();
  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key: `#message#${conversationId}` },
        });
        if (cancelled) return;
        const list = (response.data.messages || []) as Message[];
        setMessages(prev => (sameMessages(prev, list) ? prev : list));
      } catch (err) {
        console.error('useRecorderMessages: fetch failed', err);
      }
    };

    void fetchOnce();
    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [conversationId, axios]);

  return messages;
}

// Stable-identity check: skip the state update (and downstream re-renders) when
// the polled list matches what we already have.
function sameMessages(a: Message[], b: Message[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].key !== b[i].key) return false;
  }
  return true;
}
